'use client'
import { motion, useInView } from 'framer-motion'
import { useRef } from 'react'
import { useLanguage } from '@/lib/context/LanguageContext'
import { EASE, INVIEW_MARGIN } from '@/lib/constants/animations'

const SPRING = { type: 'spring', stiffness: 260, damping: 22 } as const

interface AnimProps {
  children: React.ReactNode
  delay?: number
  duration?: number
  className?: string
  once?: boolean
  margin?: string
}

// ─── Base: fade + rise ────────────────────────────────────────────────────────
export function FadeInUp({
  children,
  delay = 0,
  duration = 0.6,
  className,
  once = true,
  margin,
}: AnimProps) {
  const ref = useRef<HTMLDivElement>(null)
  const inView = useInView(ref, {
    once,
    margin: (margin ?? INVIEW_MARGIN.primary) as NonNullable<
      Parameters<typeof useInView>[1]
    >['margin'],
  })
  return (
    <motion.div
      ref={ref}
      className={className}
      initial={{ opacity: 0, y: 28 }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 28 }}
      transition={{ duration, delay, ease: EASE }}
    >
      {children}
    </motion.div>
  )
}

// ─── Blur → Focus (text materialises from fog) ───────────────────────────────
export function BlurIn({
  children,
  delay = 0,
  duration = 0.75,
  className,
  once = true,
}: AnimProps) {
  const ref = useRef<HTMLDivElement>(null)
  const inView = useInView(ref, { once, margin: INVIEW_MARGIN.secondary })
  return (
    <motion.div
      ref={ref}
      className={className}
      initial={{ opacity: 0, filter: 'blur(14px)', scale: 1.03 }}
      animate={
        inView
          ? { opacity: 1, filter: 'blur(0px)', scale: 1 }
          : { opacity: 0, filter: 'blur(14px)', scale: 1.03 }
      }
      transition={{ duration, delay, ease: EASE }}
    >
      {children}
    </motion.div>
  )
}

// ─── Slide from left ─────────────────────────────────────────────────────────
// RTL-aware: in Farsi the layout is mirrored, so the slide direction flips too.
// Keeping a fixed x would push the element off the inline-start edge (clipped by
// overflow-x-clip), so it never animates in cleanly.
export function SlideFromLeft({
  children,
  delay = 0,
  duration = 0.65,
  className,
  once = true,
}: AnimProps) {
  const ref = useRef<HTMLDivElement>(null)
  const inView = useInView(ref, { once, margin: INVIEW_MARGIN.secondary })
  const { isRTL } = useLanguage()
  const offset = isRTL ? 70 : -70
  return (
    <motion.div
      ref={ref}
      className={className}
      initial={{ opacity: 0, x: offset }}
      animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: offset }}
      transition={{ duration, delay, ease: EASE }}
    >
      {children}
    </motion.div>
  )
}

// ─── Slide from right ────────────────────────────────────────────────────────
export function SlideFromRight({
  children,
  delay = 0,
  duration = 0.65,
  className,
  once = true,
  margin,
}: AnimProps) {
  const ref = useRef<HTMLDivElement>(null)
  const inView = useInView(ref, {
    once,
    margin: (margin ?? INVIEW_MARGIN.secondary) as NonNullable<
      Parameters<typeof useInView>[1]
    >['margin'],
  })
  const { isRTL } = useLanguage()
  const offset = isRTL ? -70 : 70
  return (
    <motion.div
      ref={ref}
      className={className}
      initial={{ opacity: 0, x: offset }}
      animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: offset }}
      transition={{ duration, delay, ease: EASE }}
    >
      {children}
    </motion.div>
  )
}

// ─── Zoom in from 92% ────────────────────────────────────────────────────────
export function ZoomIn({
  children,
  delay = 0,
  duration = 0.65,
  className,
  once = true,
  margin,
}: AnimProps) {
  const ref = useRef<HTMLDivElement>(null)
  const inView = useInView(ref, {
    once,
    margin: (margin ?? INVIEW_MARGIN.secondary) as NonNullable<
      Parameters<typeof useInView>[1]
    >['margin'],
  })
  return (
    <motion.div
      ref={ref}
      className={className}
      initial={{ opacity: 0, scale: 0.92 }}
      animate={inView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.92 }}
      transition={{ duration, delay, ease: EASE }}
    >
      {children}
    </motion.div>
  )
}

// ─── Spring bounce up ────────────────────────────────────────────────────────
export function SpringUp({
  children,
  delay = 0,
  className,
  once = true,
}: Omit<AnimProps, 'duration'>) {
  const ref = useRef<HTMLDivElement>(null)
  const inView = useInView(ref, { once, margin: INVIEW_MARGIN.secondary })
  return (
    <motion.div
      ref={ref}
      className={className}
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
      transition={{ ...SPRING, delay }}
    >
      {children}
    </motion.div>
  )
}

// ─── Stagger container ───────────────────────────────────────────────────────
interface StaggerProps {
  children: React.ReactNode
  className?: string
  staggerDelay?: number
  once?: boolean
}

export function Stagger({ children, className, staggerDelay = 0.08, once = true }: StaggerProps) {
  const ref = useRef<HTMLDivElement>(null)
  const inView = useInView(ref, { once, margin: INVIEW_MARGIN.primary })
  return (
    <motion.div
      ref={ref}
      className={className}
      initial="hidden"
      animate={inView ? 'visible' : 'hidden'}
      variants={{ hidden: {}, visible: { transition: { staggerChildren: staggerDelay } } }}
    >
      {children}
    </motion.div>
  )
}

// ─── Stagger items ────────────────────────────────────────────────────────────

export function StaggerItem({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) {
  return (
    <motion.div
      className={className}
      variants={{
        hidden: { opacity: 0, y: 24 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: EASE } },
      }}
    >
      {children}
    </motion.div>
  )
}

// Scales in
export function StaggerItemZoom({
  children,
  className,
  style,
}: {
  children: React.ReactNode
  className?: string
  style?: React.CSSProperties
}) {
  return (
    <motion.div
      className={className}
      style={style}
      variants={{
        hidden: { opacity: 0, scale: 0.88 },
        visible: { opacity: 1, scale: 1, transition: { duration: 0.5, ease: EASE } },
      }}
    >
      {children}
    </motion.div>
  )
}

// Flips in on X-axis — used by TrackRecord's case-study cards for a
// cinematic-card entry. Kept after Sajjad's a11y pass trimmed unused
// variants because our TrackRecord still has both stats + cases above
// the brand wall.
export function StaggerItemFlip({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) {
  return (
    <motion.div
      className={className}
      style={{ perspective: 900 }}
      variants={{
        hidden: { opacity: 0, rotateX: 28, y: 18 },
        visible: { opacity: 1, rotateX: 0, y: 0, transition: { duration: 0.55, ease: EASE } },
      }}
    >
      {children}
    </motion.div>
  )
}

// Rises up with blur→sharp reveal (cinematic card entry) — same provenance
// as StaggerItemFlip; preserved for TrackRecord card stacks.
export function StaggerItemRise({
  children,
  className,
  style,
}: {
  children: React.ReactNode
  className?: string
  style?: React.CSSProperties
}) {
  return (
    <motion.div
      className={className}
      style={style}
      variants={{
        hidden: { opacity: 0, y: 70, scale: 0.88, filter: 'blur(4px)' },
        visible: {
          opacity: 1,
          y: 0,
          scale: 1,
          filter: 'blur(0px)',
          transition: { duration: 0.75, ease: EASE },
        },
      }}
    >
      {children}
    </motion.div>
  )
}

// Tilts in from slight angle (for cards) — RTL-aware so it mirrors in Farsi
export function StaggerItemTilt({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) {
  const { isRTL } = useLanguage()
  return (
    <motion.div
      className={className}
      style={{ perspective: 900 }}
      variants={{
        hidden: { opacity: 0, rotateY: isRTL ? -12 : 12, x: isRTL ? -24 : 24 },
        visible: { opacity: 1, rotateY: 0, x: 0, transition: { duration: 0.6, ease: EASE } },
      }}
    >
      {children}
    </motion.div>
  )
}
